import React, { useState, useEffect } from "react";
import cx from "clsx";
import VisibilitySensor from "react-visibility-sensor";
import { Parallax } from "react-scroll-parallax";
import { Container, Row, Col } from "react-grid-system";
import { isTablet } from "react-device-detect";

import TextFromLeft from "@/components/shared/text/textFromLeft";
import TextFromRight from "@/components/shared/text/textFromRight";
// import { VIS_MIN_TOP } from "@/constants";

import globalStyles from "@/styles/global.module.scss";
import sectionStyles from "./sections.module.scss";
import styles from "./section2.module.scss";

const renderTextFromLeft = (title, delayMs, props) => {
  const { isVisible } = props;

  return (
    <TextFromLeft
      visible={isVisible}
      delayMs={delayMs}
      className={cx(sectionStyles.title, globalStyles.right)}
    >
      <h3 className={sectionStyles.title}>{title}</h3>
    </TextFromLeft>
  );
};

const renderTextFromRight = (title, delayMs, props) => {
  const { isVisible } = props;

  return (
    <TextFromRight
      visible={isVisible}
      delayMs={delayMs}
      className={cx(sectionStyles.title, globalStyles.left)}
    >
      <h3 className={sectionStyles.title}>{title}</h3>
    </TextFromRight>
  );
};

const renderTitle = props => {
  const { type, title, isDesktop } = props;

  if (!title || !Array.isArray(title)) return null;

  const small = !isDesktop && !isTablet;
  let fn = type === "right" ? renderTextFromRight : renderTextFromLeft;
  if (small) fn = type === "right" ? renderTextFromLeft : renderTextFromRight;

  return !small ? (
    <Container className={globalStyles.containerMax}>
      <Row gutterWidth={0}>
        <Col xs={6} className={globalStyles.foreground}>
          {title[0] ? renderTextFromLeft(title[0], 0, props) : null}
          {title[1] ? renderTextFromLeft(title[1], 180, props) : null}
          {title[2] ? renderTextFromLeft(title[2], 360, props) : null}
        </Col>
        <Col xs={6} />
      </Row>
      <Row>
        <Col xs={6} />
        <Col xs={6} className={globalStyles.foreground}>
          {title[3] ? renderTextFromRight(title[3], 540, props) : null}
          {title[4] ? renderTextFromRight(title[4], 720, props) : null}
          {title[5] ? renderTextFromRight(title[5], 900, props) : null}
        </Col>
      </Row>
    </Container>
  ) : (
    <Container className={globalStyles.containerMax}>
      <Row gutterWidth={0}>
        <Col xs={12} className={globalStyles.foreground}>
          {title[0] ? fn(title[0], 0, props) : null}
          {title[1] ? fn(title[1], 180, props) : null}
          {title[2] ? fn(title[2], 360, props) : null}
          {title[3] ? fn(title[3], 540, props) : null}
          {title[4] ? fn(title[4], 720, props) : null}
          {title[5] ? fn(title[5], 900, props) : null}
        </Col>
      </Row>
    </Container>
  );
};

const renderSlash = props => {
  const { title, type = "right", screenClass } = props;

  if (!title || !Array.isArray(title)) return null;
  const small = ["xs", "sm"].includes(screenClass);

  let x = 6;
  if (!small) x = type === "right" ? 5 : 5;
  else x = type === "right" ? 7 : 7;
  const slashClassName =
    type === "right" ? globalStyles.forwardSlash : globalStyles.backSlash;

  return (
    <Row>
      <Col xs={12 - x} />
      <Col xs={x} className={globalStyles.foreground}>
        <div className={globalStyles.slashContainer}>
          <div className={slashClassName} />
        </div>
      </Col>
    </Row>
  );
};

const renderBgTop = props => {
  const {
    bgTop,
    bgTopX,
    bgTopY,
    bgTopClassName,
    isVisible,
    disableParallax
  } = props;

  return bgTop ? (
    <div className={cx(styles.bgTopContainer, bgTopClassName)}>
      <Parallax
        x={bgTopX || ["0px", "0px"]}
        y={bgTopY || ["-200px", "500px"]}
        disabled={!isVisible || disableParallax}
        className={styles.bgTop}
      >
        <div className={globalStyles.background}>{bgTop}</div>
      </Parallax>
    </div>
  ) : null;
};

const renderBgImg = props => {
  const { bgImg, bgImgY, bgImgClassName, isVisible, disableParallax } = props;

  return bgImg ? (
    <div className={cx(styles.bgImgContainer, bgImgClassName)}>
      <Parallax
        y={bgImgY || ["-50px", "125px"]}
        disabled={!isVisible || disableParallax}
      >
        {bgImg}
      </Parallax>
    </div>
  ) : null;
};

const renderBadge = props => {
  const {
    caption,
    imageBadge,
    isVisible,
    screenClass,
    disableParallax,
    badgePositionXs = "top",
    badgeClassName,
    badgeParallaxClassName
  } = props;

  return imageBadge ? (
    <div
      className={cx(
        styles.badgeParallax,
        styles[badgePositionXs],
        caption && styles.caption,
        badgeParallaxClassName
      )}
    >
      <Parallax
        y={screenClass === "xs" ? ["10px", "-10px"] : ["15px", "-25px"]}
        disabled={!isVisible || disableParallax}
      >
        <div className={cx(globalStyles.foreground, badgeClassName)}>
          {imageBadge}
        </div>
      </Parallax>
    </div>
  ) : null;
};

const renderBorder = props => {
  const { imageBorder, isVisible, disableParallax } = props;

  return imageBorder ? (
    <Parallax
      y={["10px", "-10px"]}
      disabled={!isVisible || disableParallax}
      className={cx(globalStyles.foreground, styles.borderContainer)}
    >
      <div className={styles.imageBorder} />
    </Parallax>
  ) : null;
};

const renderContent = props => {
  const {
    image,
    caption,
    background,
    transparent,
    imageClassName,
    paragraph,
    button,
    isDesktop,
    type = "right",
    screenClass
  } = props;

  return (
    <div className={styles.contentContainer}>
      {(type === "left" && !transparent) || background ? (
        <div className={cx(globalStyles.background, styles.contentBg)} />
      ) : null}

      <Container className={globalStyles.containerMax}>
        <Row
          gutterWidth={isDesktop ? 60 : screenClass === "md" ? 50 : undefined}
          style={{ flexDirection: type === "right" ? "row" : "row-reverse" }}
        >
          <Col xs={12} md={6}>
            {renderBgImg(props)}
            <div
              className={cx(
                globalStyles.fullWidthImage,
                styles.imgContainer,
                imageClassName
              )}
            >
              <div className={cx(globalStyles.foreground, styles.imgInner)}>
                {image}
              </div>
              {renderBorder(props)}
              {renderBadge(props)}
            </div>
            {caption ? (
              <div
                className={cx(
                  screenClass === "xs" && globalStyles.contentPaddingH,
                  globalStyles.caption,
                  globalStyles.foreground
                )}
              >
                {caption}
              </div>
            ) : null}
          </Col>
          <Col xs={12} md={6} style={{ display: "flex" }}>
            <div
              className={cx(
                globalStyles.pContainer,
                globalStyles.contentPaddingH,
                globalStyles.foreground,
                globalStyles.maxWidth,
                styles.content
              )}
            >
              {paragraph}

              {button ? (
                <div
                  className={cx(
                    sectionStyles.buttonContainer,
                    globalStyles.foreground,
                    styles.buttonContainer
                  )}
                >
                  {button}
                </div>
              ) : null}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const Section2 = props => {
  const { type = "right", isDesktop, containerClassName } = props;

  const [hasMounted, setMounted] = useState(false);
  const [isVisible, setVisible] = useState(false);
  const setIsVisible = visible =>
    visible && !isVisible ? setVisible(true) : null;

  useEffect(() => {
    if (!hasMounted) setMounted(true);

    if (!isVisible) return;

    //
  }, [isVisible]); // eslint-disable-line

  return (
    <VisibilitySensor
      delayedCall
      partialVisibility="top"
      offset={{ top: 50, bottom: 40 }}
      minTopValue={10}
      onChange={setIsVisible}
    >
      <div
        className={cx(
          sectionStyles.section,
          styles.container,
          styles[type],
          containerClassName
        )}
      >
        <div className={styles.inner}>
          <div
            className={cx(globalStyles.contentPaddingH, globalStyles.maxWidth)}
          >
            {renderTitle({ ...props, hasMounted, isVisible, isDesktop })}
          </div>

          {renderSlash({ ...props, hasMounted, isVisible, isDesktop })}

          {renderContent({ ...props, hasMounted, isVisible, isDesktop })}
        </div>
        {renderBgTop({ ...props, hasMounted, isVisible, isDesktop })}
      </div>
    </VisibilitySensor>
  );
};

export default Section2;
